import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, ArrowLeft, Calendar, MapPin, Clock, Star, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs/customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = (job) => {
    setSelectedJob(job);
    setShowReview(true);
  };

  const submitReview = async () => {
    if (!reviewData.comment.trim()) {
      toast.error('Please write a review comment');
      return;
    }

    try {
      await axios.post(
        `${API}/reviews`,
        { worker_id: selectedJob.worker_id, ...reviewData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review submitted successfully!');
      setShowReview(false);
      setReviewData({ rating: 5, comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              data-testid="back-to-home-dashboard-btn"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#003B5C] hover:text-[#002A42] transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <Wrench className="w-8 h-8" />
              <span className="text-2xl font-bold font-['Outfit']">SkillYaad</span>
            </button>

            <div className="flex items-center space-x-4">
              <span className="text-[#475569]">Hello, <span className="font-semibold text-[#003B5C]">{user?.name}</span></span>
              <Button
                data-testid="logout-btn"
                onClick={logout}
                variant="outline"
                className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white rounded-full"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#003B5C] mb-2">My Dashboard</h1>
          <p className="text-[#475569]">Manage your job requests and reviews</p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <button
            data-testid="browse-workers-btn"
            onClick={() => navigate('/workers')}
            className="bg-white rounded-xl p-6 border-2 border-transparent hover:border-[#FFB81C] transition-all duration-300 hover:shadow-lg text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2 group-hover:text-[#003B5C] transition-colors duration-300">
                  Browse Workers
                </h3>
                <p className="text-[#475569]">Find skilled professionals for your needs</p>
              </div>
              <Wrench className="w-12 h-12 text-[#FFB81C]" />
            </div>
          </button>

          <div className="bg-gradient-to-br from-[#003B5C] to-[#009B4D] rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2">Total Jobs</h3>
                <div className="text-4xl font-bold font-['Space_Grotesk']">{jobs.length}</div>
              </div>
              <Calendar className="w-12 h-12 text-white/50" />
            </div>
          </div>
        </div>

        {/* Job Requests */}
        <div className="bg-white rounded-xl shadow-sm p-8" data-testid="customer-jobs-list">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-6">My Job Requests</h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#003B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#475569]">Loading jobs...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#94A3B8] mb-4">You haven't requested any jobs yet</p>
              <Button
                data-testid="browse-workers-empty-btn"
                onClick={() => navigate('/workers')}
                className="bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full px-6 py-2 font-bold"
              >
                Browse Workers
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  data-testid={`job-card-${job.id}`}
                  className="border border-gray-200 rounded-xl p-6 hover:border-[#FFB81C]/50 transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-[#0F172A]">{job.worker_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          job.status === 'pending' ? 'bg-[#FFB81C]/20 text-[#FFB81C]' :
                          job.status === 'completed' ? 'bg-[#009B4D]/20 text-[#009B4D]' :
                          'bg-gray-200 text-gray-700'
                        }`}>
                          {job.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-[#475569] mb-3">{job.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(job.preferred_date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Requested {new Date(job.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`view-worker-btn-${job.id}`}
                        onClick={() => navigate(`/workers/${job.worker_id}`)}
                        variant="outline"
                        className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white rounded-full"
                      >
                        View Worker
                      </Button>
                      <Button
                        data-testid={`leave-review-btn-${job.id}`}
                        onClick={() => openReviewModal(job)}
                        className="bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full font-bold"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Leave Review
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent data-testid="review-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#003B5C]">
              Review {selectedJob?.worker_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-3">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    data-testid={`star-${star}-btn`}
                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= reviewData.rating
                          ? 'text-[#FFB81C] fill-[#FFB81C]'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Your Review</label>
              <Textarea
                data-testid="review-comment-input"
                value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                placeholder="Share your experience with this worker..."
                className="min-h-[120px]"
              />
            </div>
            <Button
              data-testid="submit-review-btn"
              onClick={submitReview}
              className="w-full bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full py-3 font-bold"
            >
              Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;